import { OrganizationCodes, BankCodesForAll } from '@revenuemarket/bank';
import QueryString from 'qs';
import { API } from '~apis/axios/instance';
import { AccountType } from '~models/bank/enum';
import { TaxInvoiceType } from '~models/tax-invoice';
import { ResponseBaseWithData } from '~types/response';

export type WiringConnectStatus = 'NEED_CONNECTING' | 'CONNECTED';
export interface WiringConnectingStatusResponse {
  companyId: string;
  connectStatus: WiringConnectStatus;
  additionalUrl: string;
}

export type BankType = 'KB' | 'HANA';

export type WIRING_RESULT_STATUS =
  | 'REGISTERED'
  | 'PROCESSING'
  | 'SUCCESS'
  | 'PARTIAL_FAILED'
  | 'FAILED';
export interface WiringConnectingStatusRequest {
  companyId: string;
  wiringBank: BankType;
}

export const getWiringConnectingStatus = ({
  companyId,
  wiringBank,
}: WiringConnectingStatusRequest) => {
  return API.get<ResponseBaseWithData<WiringConnectingStatusResponse>>(
    `/api/v2/companies/${companyId}/wiring/connecting/status?wiringBank=${wiringBank}`,
  );
};

export const connectWiring = ({ companyId, wiringBank }: WiringConnectingStatusRequest) => {
  return API.post<ResponseBaseWithData<{ url: string }>>(
    `/api/v2/companies/${companyId}/wiring/connecting`,
    {
      data: {
        wiringBank,
      },
    },
  );
};

export interface CheckAccountHolderRequest {
  organizationCode: BankCodesForAll;
  bankAccountNo: string;
  businessRegNo?: string;
}

export interface CheckAccountHolderResponse {
  organizationCode: OrganizationCodes;
  bankAccountNo: string;
  businessRegNo: string;
  holderName: string;
  identified: boolean;
}

export const checkAccountHolder = ({
  companyId,
  request,
}: {
  companyId: string;
  request: CheckAccountHolderRequest;
}) => {
  return API.post<ResponseBaseWithData<CheckAccountHolderResponse>>(
    `/api/v3/companies/${companyId}/banking/accounts/check-holder`,
    {
      data: request,
    },
  );
};

export interface WiringAccountListItem {
  accountId: number;
  bankCode: BankCodesForAll;
  aliasName: string;
  accountNumber: string;
  displayAccountNumber: string;
  accountType: AccountType;
  isOverDraft: boolean;
  accountBalance: number;
  title: string;
}

export const getWiringAccountList = ({
  companyId,
  includeBalance = true,
}: {
  companyId: string;
  includeBalance?: boolean;
}) => {
  return API.get<ResponseBaseWithData<{ items: WiringAccountListItem[] }>>(
    `/api/v2/companies/${companyId}/wiring/available-account?includeBalance=${includeBalance}`,
  );
};

export type WiringType = 'NORMAL' | 'BULK' | 'SALARY';
export interface ExecuteWiringRequest {
  wiringBank: 'KB' | 'HANA';
  title: string;
  scheduledAt?: string;
  isCompressed: boolean;
  compressedMessage?: string;
  wiringType: WiringType;
  accountId: number;
  accountNumber: string;
  recordList: IRecordListItem[];
}

export interface IRecordListItem {
  receiverBankCode: BankCodesForAll | null;
  receiverAccountNumber: string;
  amount: number;
  receiverDescription?: string;
  senderDescription?: string;
  recipientEngNam?: string;
  recipientPhoneNumber?: string;
  currencyCode?: string;
  businessEntityId?: number;
  categoryId?: number;
  taxInvoiceKey?: {
    taxInvoiceId: string;
    taxInvoiceType: TaxInvoiceType;
  };
  receiverName?: string;
}

export const retryExistWiring = ({
  companyId,
  wiringId,
}: {
  companyId: string;
  wiringId: number;
}) => {
  return API.post<ResponseBaseWithData<{ wiringId: number; url: string }>>(
    `/api/v2/companies/${companyId}/wiring/${wiringId}/retry`,
  );
};

export const executeWiring = ({
  companyId,
  request,
}: {
  companyId: string;
  request: ExecuteWiringRequest;
}) => {
  return API.post<
    ResponseBaseWithData<{
      url: string;
      wiringId: number;
      data: {
        ott: string;
        mntaccInfo: string;
        mntaccInfoLen: number;
      } | null;
    }>
  >(`/api/v2/companies/${companyId}/wiring`, {
    data: request,
  });
};

export enum KbWiringResultCode {
  UBFN0197 = 'UBFN0197',
  UBFN0270 = 'UBFN0270',
  UBFN0271 = 'UBFN0271',
  UBFN0272 = 'UBFN0272',
  UBFN0273 = 'UBFN0273',
}

export interface WiringResult {
  accountAlias: string;
  id: number;
  wiringBank: 'KB' | 'HANA';
  companyId: string;
  isCompressed: boolean;
  compressedMessage: string | null;
  scheduledAt: string | null;
  title: string;
  wiringType: WiringType;
  accountId: number;
  accountNumber: string;
  wiringBankKey: string;
  recordList: WiringResultItem[];
  status: WIRING_RESULT_STATUS;
  createdBy: ICreatedBy;
  createdAt: string;
  updatedAt: string;
  resultCode?: KbWiringResultCode;
  resultMessage?: string;
}
export interface WiringResultItem {
  id: number;
  orderInWiring: number;
  companyId: string;
  wiringId: number;
  wiringBank: BankCodesForAll;
  receiverBankCode: BankCodesForAll;
  senderAccountNumber: string;
  receiverAccountNumber: string;
  status: 'SUCCESS' | 'FAILED' | 'WAITING';
  memo?: string;
  amount: number;
  receiverDescription: string;
  senderDescription: string;
  recipientEngName: string;
  receiverName: string;
  recipientPhoneNumber: string;
  currencyCode: string;
  businessEntityId: number;
  categoryLabelKey: string;
  customLabelId: number;
  systemMessage: string;
  transactionAt: string;
  createdAt: string;
  updatedAt: string;
  failed: boolean;
  waiting: boolean;
  successful: boolean;
  recordId?: number;
  taxInvoiceKey: {
    taxInvoiceId: string;
    taxInvoiceType: TaxInvoiceType;
  };
}
interface ICreatedBy {
  userId: string;
  userName: string;
}

export const checkWiringExecution = ({
  companyId,
  wiringId,
}: {
  companyId: string;
  wiringId: number;
}) => {
  return API.post<ResponseBaseWithData<WiringResult>>(
    `/api/v2/companies/${companyId}/wiring/${wiringId}/check-execution`,
  );
};

export const getRencentWiring = ({
  companyId,
  keyword,
}: {
  companyId: string;
  keyword?: string;
}) => {
  const qs = `?${QueryString.stringify({
    keyword,
  })}`;
  return API.get<ResponseBaseWithData<{ items: WiringResult[] }>>(
    `/api/v2/companies/${companyId}/wiring/recent${qs}`,
  );
};

export const getWiringResult = ({
  companyId,
  wiringId,
}: {
  companyId: string;
  wiringId: number;
}) => {
  return API.get<ResponseBaseWithData<WiringResult>>(
    `/api/v2/companies/${companyId}/wiring/${wiringId}`,
  );
};

export interface SearchReceiverAccountList {
  items: SearchReceiverAccountListItem[];
}

export type SearchReceiverAccountType = 'BUSINESS_ENTITY' | 'SELF' | 'NONE';
export interface SearchReceiverAccountListItem {
  bankCode: BankCodesForAll;
  accountNumber: string;
  accountHolderName: string;
  type: SearchReceiverAccountType;
  recentSent: boolean;
  businessEntityId: number;
  businessEntityRegNo?: string;
}

export const getSearchReceiverAccounts = ({
  companyId,
  keyword,
}: {
  companyId: string;
  keyword: string;
}) => {
  return API.get<ResponseBaseWithData<SearchReceiverAccountList>>(
    `/api/v2/companies/${companyId}/wiring/receiver-account/search?keyword=${keyword}`,
  );
};

export interface ReserveLabelingRequest {
  wiringId: number;
  recordLabelingList: IRecordLabelingListItem[];
}
export interface IRecordLabelingListItem {
  recordId: number;
  categoryLabelKey?: string;
  businessEntityId?: number;
  customLabelId?: number;
}

export const reserveLabeling = ({
  companyId,
  request,
}: {
  companyId: string;
  request: ReserveLabelingRequest;
}) => {
  return API.post(`/api/v2/companies/${companyId}/wiring/label/reserve`, {
    data: request,
  });
};

export const getStatusCount = ({
  companyId,
  from,
  to,
}: {
  companyId: string;
  from: string;
  to: string;
}) => {
  return API.get<ResponseBaseWithData<Record<WIRING_RESULT_STATUS, number>>>(
    `/api/v2/companies/${companyId}/wiring/status-count?from=${from}&to=${to}`,
  );
};

export const getWiringResultList = ({
  companyId,
  from,
  to,
  userId,
  status,
  accountId,
}: {
  companyId: string;
  from: string;
  to: string;
  userId?: string;
  status?: WIRING_RESULT_STATUS;
  accountId?: number;
}) => {
  const qs = QueryString.stringify({
    from,
    to,
    userId,
    status,
    accountId,
  });

  return API.get<ResponseBaseWithData<WiringResult[]>>(
    `/api/v2/companies/${companyId}/wiring?${qs}`,
  );
};

export const getASTDownloadUrl = ({
  companyId,
  osType,
}: {
  companyId: string;
  osType: 'MAC' | 'WINDOWS';
}) => {
  return API.get<ResponseBaseWithData<{ url: string }>>(
    `/api/v2/companies/${companyId}/ast/file?osType=${osType}`,
  );
};

export const cancelWiring = ({ companyId, wiringId }: { companyId: string; wiringId: number }) => {
  return API.post<
    ResponseBaseWithData<{
      data: {
        ott: string;
        mntaccInfoLen: number;
        mntaccInfo: string;
      };
      url: string;
      wiringId: number;
    }>
  >(`/api/v2/companies/${companyId}/wiring/${wiringId}/cancel`);
};

export const deleteWiring = ({ companyId, wiringId }: { companyId: string; wiringId: number }) => {
  return API.delete<ResponseBaseWithData>(`/api/v2/companies/${companyId}/wiring/${wiringId}`);
};

export const deleteWiringList = ({
  companyId,
  wiringIds,
}: {
  companyId: string;
  wiringIds: number[];
}) => {
  const qs = QueryString.stringify(
    {
      wiringIds,
    },
    { arrayFormat: 'repeat' },
  );
  return API.delete<ResponseBaseWithData>(`/api/v2/companies/${companyId}/wiring?${qs}`);
};
